import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Panel, Separator } from 'react-resizable-panels'
import { DisplayWindow, DisplayHeader, Display } from '../display'
import PdfViewer from '../pdf/PdfViewer'
import { standardPdfUrl } from '../../services/pdfs'
import { standardId } from '../../utils/standardRef'
import { notebookFileUrl, updateNotebook } from '../../services/notebooks'
import {
  attachmentIds,
  itemIdOf,
  mergeLegacyAttachments,
  serializeBlocks,
} from './blockUtils'
import NotebookEditor from './NotebookEditor'
import './NotebookDisplay.css'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconChevrons = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'right' ? (
      <>
        <polyline points="7 6 13 12 7 18" />
        <polyline points="13 6 19 12 13 18" />
      </>
    ) : (
      <>
        <polyline points="17 6 11 12 17 18" />
        <polyline points="11 6 5 12 11 18" />
      </>
    )}
  </svg>
)

// Editor | PDF split for one notebook page. Title and blocks autosave
// (debounced). Selecting a linked item opens its PDF; a page with one item
// opens that PDF by default.
function NotebookPanes({
  page,
  panes,
  onPanesChange,
  onSaved,
  onCreatePage,
  onOpenPage,
  onDeletePage,
  pageTitle,
  pagePath = [],
}) {
  const defaultLayout = useMemo(
    () => ({ editor: panes?.editor ?? 55, viewer: panes?.viewer ?? 45 }),
    [panes]
  )

  const [title, setTitle] = useState(page.title || '')
  const [blocks, setBlocks] = useState(() => mergeLegacyAttachments(page.blocks, page).blocks)
  const [viewer, setViewer] = useState(() =>
    page.items?.[0] ? { kind: 'item', item: page.items[0] } : null
  )

  const editorPanelRef = useRef(null)
  const viewerPanelRef = useRef(null)
  const [collapsed, setCollapsed] = useState('none')
  const saveTimer = useRef(null)
  const pendingPatch = useRef({})
  const pageIdRef = useRef(page._id)

  // Rehydrate local draft when the window loads a different page. Flush any
  // pending autosave for the page we are leaving so a fast switch does not drop it.
  useEffect(() => {
    const id = page._id
    pageIdRef.current = id
    setTitle(page.title || '')
    const composed = mergeLegacyAttachments(page.blocks, page)
    setBlocks(composed.blocks)
    setViewer(page.items?.[0] ? { kind: 'item', item: page.items[0] } : null)
    if (composed.migrated) {
      const ids = attachmentIds(composed.blocks)
      pendingPatch.current = {
        blocks: serializeBlocks(composed.blocks),
        ...ids,
      }
    }
    return () => {
      clearTimeout(saveTimer.current)
      const patch = pendingPatch.current
      pendingPatch.current = {}
      if (Object.keys(patch).length) {
        updateNotebook(id, patch).catch(() => {})
      }
    }
  }, [page._id])

  const flushSave = useCallback(() => {
    const patch = pendingPatch.current
    pendingPatch.current = {}
    if (!Object.keys(patch).length) return
    const id = pageIdRef.current
    updateNotebook(id, patch)
      .then((updated) => {
        if (pageIdRef.current === id) onSaved?.(updated)
      })
      .catch(() => {})
  }, [onSaved])

  const scheduleSave = useCallback(
    (patch) => {
      pendingPatch.current = { ...pendingPatch.current, ...patch }
      clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(flushSave, 500)
    },
    [flushSave]
  )

  useEffect(
    () => () => {
      clearTimeout(saveTimer.current)
    },
    []
  )

  const handleTitleChange = (value) => {
    setTitle(value)
    scheduleSave({ title: value.trim() || 'Untitled' })
  }

  const handleBlocksChange = (next) => {
    setBlocks(next)
    scheduleSave({ blocks: serializeBlocks(next), ...attachmentIds(next) })
  }

  const onEditorResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'editor' : c === 'editor' ? 'none' : c))
  }, [])
  const onViewerResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'viewer' : c === 'viewer' ? 'none' : c))
  }, [])

  const toggleEditorMaximize = () => {
    if (collapsed === 'viewer') viewerPanelRef.current?.expand()
    else viewerPanelRef.current?.collapse()
  }
  const toggleViewerMaximize = () => {
    if (collapsed === 'editor') editorPanelRef.current?.expand()
    else editorPanelRef.current?.collapse()
  }

  const itemBlocks = blocks.filter((block) => block.type === 'item' && block.item)
  const selectedItem =
    viewer?.kind === 'item'
      ? itemBlocks.find((block) => itemIdOf(block.item) === itemIdOf(viewer.item))?.item ||
        viewer.item
      : null
  const selectedPdf = viewer?.kind === 'pdf' ? viewer : null

  return (
    <div className="nb-display">
      <Group
        orientation="horizontal"
        className="nb-panelgroup"
        defaultLayout={defaultLayout}
        onLayoutChanged={onPanesChange}
      >
        <Panel
          id="editor"
          className="nb-pane"
          defaultSize="55%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={editorPanelRef}
          onResize={onEditorResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                actions={
                  <button
                    type="button"
                    title={collapsed === 'viewer' ? 'Restore split view' : 'Expand editor pane'}
                    aria-label={
                      collapsed === 'viewer' ? 'Restore split view' : 'Expand editor pane'
                    }
                    className={collapsed === 'viewer' ? 'is-active' : ''}
                    onClick={toggleEditorMaximize}
                  >
                    <IconChevrons dir={collapsed === 'viewer' ? 'left' : 'right'} />
                  </button>
                }
              >
                <nav className="nb-viewer-loc" aria-label="Page">
                  {(pagePath.length ? pagePath : [page]).map((entry, index, list) => {
                    const last = index === list.length - 1
                    const label = last ? title.trim() || 'Untitled' : entry.title?.trim() || 'Untitled'
                    return (
                      <span className="nb-viewer-loc-seg" key={entry._id || index}>
                        {index > 0 && <span className="nb-viewer-loc-sep">/</span>}
                        <button
                          type="button"
                          className="nb-viewer-loc-link"
                          title={last ? label : `Open ${label}`}
                          onClick={() => {
                            if (!last) onOpenPage?.(entry)
                          }}
                        >
                          {label}
                        </button>
                      </span>
                    )
                  })}
                </nav>
              </DisplayHeader>
            }
          >
            <Display padded>
              <NotebookEditor
                title={title}
                blocks={blocks}
                onTitleChange={handleTitleChange}
                onBlocksChange={handleBlocksChange}
                selectedItemId={
                  selectedPdf
                    ? `pdf:${selectedPdf.fileId}`
                    : selectedItem
                      ? itemIdOf(selectedItem)
                      : ''
                }
                onSelectItem={(item) => setViewer({ kind: 'item', item })}
                onSelectPdf={(file) => setViewer({ kind: 'pdf', ...file })}
                onCreatePage={onCreatePage}
                onOpenPage={onOpenPage}
                onDeletePage={onDeletePage}
                pageTitle={pageTitle}
                wide={collapsed === 'viewer'}
              />
            </Display>
          </DisplayWindow>
        </Panel>

        <Separator className="nb-resize-handle" />

        <Panel
          id="viewer"
          className="nb-pane"
          defaultSize="45%"
          minSize="20%"
          collapsible
          collapsedSize="0%"
          panelRef={viewerPanelRef}
          onResize={onViewerResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                actions={
                  <button
                    type="button"
                    title={collapsed === 'editor' ? 'Restore split view' : 'Expand PDF pane'}
                    aria-label={
                      collapsed === 'editor' ? 'Restore split view' : 'Expand PDF pane'
                    }
                    className={collapsed === 'editor' ? 'is-active' : ''}
                    onClick={toggleViewerMaximize}
                  >
                    <IconChevrons dir={collapsed === 'editor' ? 'right' : 'left'} />
                  </button>
                }
              >
                <span className="nb-viewer-title" title={selectedPdf?.name || selectedItem?.label}>
                  {selectedPdf?.name || selectedItem?.label || 'Viewer'}
                </span>
              </DisplayHeader>
            }
          >
            <Display fill>
              {selectedPdf?.fileId ? (
                <PdfViewer key={selectedPdf.fileId} fileUrl={notebookFileUrl(selectedPdf.fileId)} />
              ) : selectedItem?.standard ? (
                <PdfViewer
                  key={selectedItem._id}
                  fileUrl={standardPdfUrl(selectedItem.standard)}
                  page={selectedItem.pageStart}
                  pageRange={
                    selectedItem.pageStart != null
                      ? [selectedItem.pageStart, selectedItem.pageEnd]
                      : undefined
                  }
                  standard={standardId(selectedItem.standard)}
                />
              ) : (
                <div className="display-empty">
                  Add an item or PDF block to view it here.
                </div>
              )}
            </Display>
          </DisplayWindow>
        </Panel>
      </Group>
    </div>
  )
}

export default NotebookPanes
